import React, { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { Card, Dialog, DialogTitle, DialogContent, DialogActions, Stack, Table as MuiTable, Tabs, Tab } from "@mui/material";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import { TableCell } from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import colors from "assets/theme/base/colors";
import borders from "assets/theme/base/borders";
import AdminWPListTableUser from "../list/AdminWPListTableUser";
import allCommonCodeMap from "api/commonCode/allCommonCodeMap";
import { customAxios } from "../../../../axios/CustomAxios";
import { myIpAddress } from "config";
import { AmountFormat } from "utils/AmountFormat";
import { useRecoilState } from "recoil";
import wpAdditionalHistory from "atom/wp/wpAdditionalHistory";

const UserSettingPanel = () => {
  const { light } = colors;
  const { borderWidth } = borders;

  const todayDate = new Date();
  const year = todayDate.getFullYear().toString();

    const queryClient = useQueryClient();

  const { data: codeMap } = useQuery("allCommonCodeMap", allCommonCodeMap, {
    select: (res) => res.data,
  });

  const fetchMembers = () =>
    customAxios.post(myIpAddress + "/wp/v2/userList", null, { params: { year } });

    const additionalSaveMutation = useMutation(
        (data) => customAxios.post(myIpAddress + "/wp/v2/addPoint/add", data,), // 파라미터 전송 방식에 따라 수정 (body or params)
        {
            onSuccess: (data, variables) => {
                // 저장 성공 시 목록 새로고침
                queryClient.invalidateQueries(["wpUserList", year]);

                // (옵션) 전역 상태 이력 업데이트가 필요하다면 여기서 수행
                // setHistory((prev) => [variables, ...prev]);

                handleClose();
                alert("추가 지급이 완료되었습니다.");
            },
            onError: (error) => {
                console.error("Additional Save Error:", error);
                alert("추가 지급 처리에 실패했습니다.");
            }
        }
    );
  const { data: members, isLoading } = useQuery(["wpUserList", year], fetchMembers, {
    select: (res) => res.data,
  });



  const columns = useMemo(
    () => [
        { key: "user", name: "사원", align: "left", width: "20%" },
        { key: "used_point", name: "사용한 금액", align: "center", width: "15%" },
      { key: "available_point", name: "현재 사용 가능 금액", align: "center", width: "15%" },
      { key: "total_point", name: "이번 연도 금액", align: "center", width: "20%" },
      { key: "last_distribute_date", name: "마지막 포인트 지급일", align: "center", width: "20%" },
      { key: "currentUsage", name: "사용 현황", align: "center", width: "10%" }
    ],
    []
  );

  const [selectedUser, setSelectedUser] = useState(null);
  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState("");

  // 다이얼로그 내 탭
  const [dialogTab, setDialogTab] = useState(0); // 0: 기본 설정, 1: 추가 지급

  const [form, setForm] = useState({
    // 기본 설정 탭
    total_point: "",
    used_point: "",
    available_point: "",
    // 추가 지급 탭
    amount: "",
    months: "",
    reason: "",
    applyDate: "",
  });

  // 추가 지급 이력 전역 상태
  const [history, setHistory] = useRecoilState(wpAdditionalHistory);

  const handleRowClick = (user) => {
    setSelectedUser(user);
    setForm({
      total_point: user?.total_point || "",
      used_point: user?.used_point || "",
      available_point: user?.available_point || "",
      amount: "",
      months: "",
      reason: "",
      applyDate: "",
    });
    setDialogTab(0);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedUser(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // 검색 텍스트 기준으로 사용자 필터링
  const filteredMembers = useMemo(() => {
    if (!members) return [];
    const q = (searchText || "").trim().toLowerCase();
    if (!q) return members;
    return members.filter((m) => {
      const teamLabel = m["mem_team"] && codeMap ? codeMap[m["mem_team"]]?.info : "";
      const rankLabel = m["mem_rank"] && codeMap ? codeMap[m["mem_rank"]]?.info : "";
      const hay = `${m.mem_name || ""} ${teamLabel || ""} ${rankLabel || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [members, codeMap, searchText]);

  // 기본 설정 저장 (더미)
  const handleSaveBasic = async () => {
    // TODO: 기본 설정 저장 API 확정 후 연동
    // eslint-disable-next-line no-console
    console.log("[User WP Basic Save]", {
      mem_no: selectedUser?.mem_no,
      total_point: form.total_point,
      used_point: form.used_point,
      available_point: form.available_point,
    });
    handleClose();
  };

  // 추가 지급 저장 (더미 + 전역 이력 반영)
  const handleSaveAdditional = async () => {
    const amountNum = parseInt(form.amount, 10);
    if (isNaN(amountNum) || amountNum <= 0) {
      // eslint-disable-next-line no-alert
      alert("금액을 1원 이상 정수로 입력하세요.");
      return;
    }
    const now = new Date();
    const entry = {
      mem_no: selectedUser?.mem_no,
        additional_point: amountNum,
        monthlyPoint : 0,
        distributionMonths: form.months ? parseInt(form.months, 10) || 0 : 0,
      reason: form.reason || "",
        startYearMonth: form.applyDate ? `${form.applyDate}-01` : null,
    };
      additionalSaveMutation.mutate(entry);
  };

  return (
    <SoftBox p={2}>
      <SoftTypography variant="h6" fontWeight="bold" mb={1}>
        사용자 설정
      </SoftTypography>
      <SoftTypography variant="button" color="text" mb={2}>
        사용자 리스트에서 대상을 클릭하면 설정 팝업이 열립니다.
      </SoftTypography>

      {/* 검색 입력 */}
      <SoftBox mb={2} display="flex" alignItems="center" gap={1}>
        <SoftInput
          placeholder="이름/직급 검색"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ maxWidth: 320 }}
        />
        {members && (
          <SoftTypography variant="caption" color="text">
            전체 {members.length}명 · 결과 {filteredMembers.length}명
          </SoftTypography>
        )}
      </SoftBox>

      <TableContainer sx={{ maxHeight: 520 }}>
        <MuiTable stickyHeader>
          <thead>
            <TableRow>
              {columns.map((c) => (
                <TableCell
                  key={c.key}
                  align={c.align}
                  sx={{
                    width: c.width,
                    borderBottom: `${borderWidth[1]} solid ${light.main}`,
                    position: "sticky",
                    top: 0,
                    zIndex: 2,
                    backgroundColor: ({ palette }) => palette.background.paper,
                  }}
                >
                  <SoftTypography variant="button" color="text" fontWeight="bold">
                    {c.name}
                  </SoftTypography>
                </TableCell>
              ))}
            </TableRow>
          </thead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  <SoftTypography variant="button" color="text">로딩 중...</SoftTypography>
                </TableCell>
              </TableRow>
            )}

            {!isLoading && members && members.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  <SoftTypography variant="button" color="text">데이터가 없습니다.</SoftTypography>
                </TableCell>
              </TableRow>
            )}

            {!isLoading && members && filteredMembers.length === 0 && members.length > 0 && (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  <SoftTypography variant="button" color="text">검색 결과가 없습니다.</SoftTypography>
                </TableCell>
              </TableRow>
            )}

            {!isLoading && filteredMembers && filteredMembers.map((m) => {
              const amount = (parseInt(m["used_point"]) / parseInt(m["total_point"] || 1)) * 100;
              const usagePercent = isNaN(amount) ? 0 : amount >= 1 || amount === 0 ? parseInt(amount) : amount.toFixed(2);
              const team = m["mem_team"] && codeMap ? codeMap[m["mem_team"]]?.info : "";
              const rank = m["mem_rank"] && codeMap ? codeMap[m["mem_rank"]]?.info : "";
              const usedPoint = AmountFormat(parseInt(m["used_point"]));
              const availablePoint = AmountFormat(parseInt(m["available_point"]));
              const totalPoint = AmountFormat(parseInt(m["total_point"]));
              const lastDistributedDate = m["last_distribute_date"] && new Date(m["last_distribute_date"]).toLocaleDateString("ko-KR");
              return (
                <TableRow key={m.mem_no} hover sx={{ cursor: "pointer" }} onClick={() => handleRowClick(m)}>
                  <TableCell align="left" style={{ borderBottom: `${borderWidth[1]} solid ${light.main}` }}>
                    <AdminWPListTableUser name={m.mem_name} team={team} rank={rank} imageUrl={m.imageUrl} />
                  </TableCell>
                  <TableCell align="center" style={{ borderBottom: `${borderWidth[1]} solid ${light.main}` }}>
                    <SoftTypography variant="button" color="dark">{usedPoint} 원</SoftTypography>
                  </TableCell>
                  <TableCell align="center" style={{ borderBottom: `${borderWidth[1]} solid ${light.main}` }}>
                    <SoftTypography variant="button" color="dark">{availablePoint} 원</SoftTypography>
                  </TableCell>
                  <TableCell align="center" style={{ borderBottom: `${borderWidth[1]} solid ${light.main}` }}>
                    <SoftTypography variant="button" color="dark">{totalPoint} 원</SoftTypography>
                  </TableCell>
                  <TableCell align="center" style={{ borderBottom: `${borderWidth[1]} solid ${light.main}` }}>
                    <SoftTypography variant="button" color="dark">{lastDistributedDate}</SoftTypography>
                  </TableCell>
                  <TableCell align="center" style={{ borderBottom: `${borderWidth[1]} solid ${light.main}` }}>
                    <SoftTypography variant="button" color="dark">{usagePercent}%</SoftTypography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </MuiTable>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>
          <SoftTypography variant="h6" fontWeight="bold">
            사용자 설정: {selectedUser?.mem_name || ""}
          </SoftTypography>
        </DialogTitle>
        <DialogContent>
          {/* 다이얼로그 내부 탭 */}
          <Tabs value={dialogTab} onChange={(e, v) => setDialogTab(v)} sx={{ mb: 2 }}>
            <Tab label="기본 설정" />
            <Tab label="추가 지급" />
          </Tabs>

          {dialogTab === 0 && (
            <Stack spacing={2} mt={1}>
              <SoftBox>
                <SoftTypography variant="caption" color="text" mb={0.5} display="block">
                  이번 연도 금액(원)
                </SoftTypography>
                <SoftInput type="number" name="total_point" value={form.total_point} onChange={handleChange} placeholder="예: 500000" />
              </SoftBox>
              <SoftBox>
                <SoftTypography variant="caption" color="text" mb={0.5} display="block">
                  사용 한 금액(원)
                </SoftTypography>
                <SoftInput type="number" name="used_point" value={form.used_point} onChange={handleChange} placeholder="예: 500000" />
              </SoftBox>
              <SoftBox>
                <SoftTypography variant="caption" color="text" mb={0.5} display="block">
                  현재 사용 가능 금액(원)
                </SoftTypography>
                <SoftInput type="number" name="available_point" value={form.available_point} onChange={handleChange} placeholder="예: 500000" />
              </SoftBox>
            </Stack>
          )}

          {dialogTab === 1 && (
            <Stack spacing={2} mt={1}>
              <SoftBox>
                <SoftTypography variant="caption" color="text" mb={0.5} display="block">
                  금액(원)
                </SoftTypography>
                <SoftInput type="number" name="amount" value={form.amount} onChange={handleChange} placeholder="예: 10000" />
              </SoftBox>
              <SoftBox>
                <SoftTypography variant="caption" color="text" mb={0.5} display="block">
                  사유(선택)
                </SoftTypography>
                <SoftInput name="reason" value={form.reason} onChange={handleChange} placeholder="지급 사유를 입력하세요" />
              </SoftBox>
              <SoftBox>
                <SoftTypography variant="caption" color="text" mb={0.5} display="block">
                  분할 지급 개월(선택)
                </SoftTypography>
                <SoftInput type="number" name="months" value={form.months} onChange={handleChange} placeholder="예: 3 (미입력 시 일시 지급)" />
              </SoftBox>
              <SoftBox>
                <SoftTypography variant="caption" color="text" mb={0.5} display="block">
                  적용 시점(선택)
                </SoftTypography>
                <SoftInput type="month" name="applyDate" value={form.applyDate} onChange={handleChange} placeholder="미입력 시 즉시 지급" />
              </SoftBox>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <SoftButton variant="outlined" color="secondary" onClick={handleClose}>취소</SoftButton>
          {dialogTab === 0 ? (
            <SoftButton variant="gradient" color="info" onClick={handleSaveBasic}>저장</SoftButton>
          ) : (
            <SoftButton variant="gradient" color="info" onClick={handleSaveAdditional}>저장</SoftButton>
          )}
        </DialogActions>
      </Dialog>
    </SoftBox>
  );
};

export default UserSettingPanel;
